import { useQuery } from "@tanstack/react-query";
import { getWishlist } from "@server/requests/wishlist/getWishlist";
import { getAnonymousWishlist } from "@server/requests/anonymousWishlist/getAnonymousWishlist";
import { getWishlistCookies } from "@server/hooks/wishlist/utils";
import { useCallback } from "react";

export function useWishlist() {
  const { data, isFetching, isLoading, isError } = useQuery({
    queryKey: ["wishlist"],
    queryFn: async () => {
      const { token, wishlistId, wishlistToken = "" } = getWishlistCookies();
      if (typeof token === "string" && Boolean(token)) {
        const response = await getWishlist({ token });
        if (!response.getWishlist.isError) {
          return {
            items: response.getWishlist.data?.wishlistItems,
            total: response.getWishlist.data?.metadata?.totalItems,
          };
        }
        return undefined;
      }
      if (wishlistId && wishlistToken) {
        const response = await getAnonymousWishlist({
          wishlistId,
          wishlistToken,
        });
        if (!response.getAnonymousWishlist.isError) {
          return {
            items: response.getAnonymousWishlist.data?.wishlistItems,
            total: response.getAnonymousWishlist.data?.metadata?.totalItems,
          };
        }
      }
      return undefined;
    },
  });

  const getWishlistItem = useCallback(
    (payload: { variantId: string } | { productId: string }) => {
      if (!data?.items?.length) return undefined;

      return data?.items?.find((item) => {
        if ("variantId" in payload) {
          return item.variantId === payload.variantId;
        }
        return item.productId === payload.productId;
      });
    },
    [data],
  );

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading,
    isError,
    isFetching,
    getWishlistItem,
  };
}
